'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useContext, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import Button from '@/components/Button';
import { TCard } from '@/containers/EnterpriseAccount/components/HeroNewOffer/components/Cards';
import { AmplitudeContext } from '@/contexts/amplitude';
import { BannerProps } from '@/types/cms';

import HeroModal from '../NewHero/HeroModal';

interface HeroProps
  extends Omit<
    BannerProps,
    'id' | 'referId' | 'machineImage' | 'iconsDescriptions'
  > {
  id: string;
}
export const resourceCards: TCard[] = [
  {
    firstText: 'De 1,22% por',
    mainText: '0,79%',
    lastText: 'no Débito',

    lineThrough: true,
    fontMainText: true,
  },
  {
    firstText: 'De 3,59% por',
    mainText: '2,89%',
    lastText: 'no Crédito 1x',

    lineThrough: true,
    fontMainText: true,
  },
];

const HeroBlackFriday: React.FC<HeroProps> = ({
  bannerImages,
  titleDescriptionTag,
  buttons,
}) => {
  const { analytics } = useContext(AmplitudeContext);
  const [ref, inView] = useInView({
    triggerOnce: true,
  });

  const [showModal, setShowModal] = useState(false);
  const closeModal = () => {
    setShowModal(false);
  };

  useEffect(() => {
    if (inView) {
      analytics?.track({
        event_type: 'section viewed',
        event_properties: {
          name: 'section viewed',
          description:
            'Evento disparado quando o usuário visualiza uma sessão especifica da pagina',
          section_reference:
            'Taxas promocionais pro seu negócio passar a Black Friday',
        },
      });
    }
  }, [analytics, inView, titleDescriptionTag.title]);

  function trackCTA(reference: string) {
    analytics?.track({
      event_type: 'cta stone',
      event_properties: {
        name: 'cta stone',
        description: 'Evento disparado no clique no cta do hero',
        section_reference:
          'Taxas promocionais pro seu negócio passar a Black Friday',
        cta_reference: reference,
      },
    });
  }

  return (
    <>
      <div
        className="flex justify-center items-center bg-display-0"
        id="Context_heroHomePage"
        ref={ref}
      >
        <div className="flex relative flex-col md:flex-row justify-end md:justify-center items-center w-full md:h-[616px] lg:h-[656px]">
          <div className="hidden lg:flex w-full h-[656px]">
            <Image
              src={bannerImages?.web?.path}
              alt={bannerImages?.web?.alternativeText}
              width={1920}
              height={656}
              quality={80}
              priority
              className="object-cover"
              layout="responsive"
            />
          </div>
          <div className="hidden md:flex lg:hidden w-full h-[616px]">
            <Image
              src={bannerImages?.tablet?.path}
              alt={bannerImages?.tablet?.alternativeText}
              width={1920}
              height={616}
              quality={80}
              priority
              className="object-cover"
            />
          </div>
          <div className="flex md:hidden relative bottom-24 order-1 w-full h-full">
            <Image
              src={bannerImages?.mobile?.path}
              alt={bannerImages?.mobile?.alternativeText}
              width={1057}
              height={285}
              quality={80}
              priority
              className="object-cover"
            />
          </div>
          <div className="flex md:absolute left-0 z-10 lg:justify-end lg:items-center p-24 lg:p-40 md:py-40 w-full md:w-1/2 lg:w-auto xl:w-1/2 bg-[#00231B] md:rounded-none md:rounded-r-base lg:rounded-r-6xl rounded-b-2xl">
            <div className="flex flex-col gap-24 lg:items-end w-full">
              <div className="flex flex-col gap-16 justify-center lg:justify-start items-center lg:items-start w-full lg:max-w-[601px]">
                <div className="flex flex-col gap-4 lg:gap-16">
                  <h1 className="md:block h-full font-display text-5xl lg:text-[64px] font-black leading-[44px] lg:leading-[56px] text-center lg:text-left text-stone-300">
                    Black Friday
                  </h1>
                  <h5 className="max-w-[526px] font-display text-xl lg:text-2xl leading-5 lg:leading-6 text-center lg:text-left text-display-0">
                    Taxas promocionais pro seu negócio passar a Black Friday
                    <span className="text-stone-300"> no verde</span>
                  </h5>
                </div>
                <div className="flex flex-col lg:w-full xl:w-auto">
                  <div className="flex flex-wrap gap-8 lg:gap-y-16 lg:w-[471px] lg:max-w-[471px]">
                    <p className="w-full font-sharon text-base md:text-lg md:leading-[26px] text-center lg:text-left text-display-0">
                      Com taxas a partir de
                    </p>
                    <div className="grid grid-cols-2 gap-16 w-full lg:w-auto">
                      {resourceCards.map((item, index) => (
                        <div
                          key={index}
                          className="flex flex-col gap-4 justify-center items-center p-16 lg:w-[170px] rounded-base lg:rounded-md border-2 border-stone-600"
                        >
                          <h1 className="font-display text-5xl md:text-5xl lg:text-7xl font-black md:leading-[44px] lg:leading-10 text-stone-300">
                            {item.mainText}
                          </h1>
                          <p className="font-sharon text-base font-semibold md:font-bold leading-4 text-display-0">
                            {item.lastText}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                  <p className="mt-8 lg:mt-24 text-base text-center lg:text-left text-display-0">
                    Confira{' '}
                    <span
                      onClick={() => {
                        setShowModal(true);
                        analytics?.track({
                          event_type: 'cta stone',
                          event_properties: {
                            name: 'cta stone',
                            description:
                              'Evento disparado quando o usuário clicar nos ctas da LP',
                            cta_reference: 'Confira mais detalhes',
                          },
                        });
                      }}
                      className="decoration-slice text-base text-stone-300 underline cursor-pointer"
                    >
                      mais detalhes da oferta
                    </span>
                  </p>
                  <div className="flex lg:grid flex-col lg:flex-row grid-cols-2 gap-16 lg:gap-24 mt-16 lg:mt-24 w-full">
                    {buttons?.map(({ color, label, link }, index) => (
                      <Button
                        key={index}
                        href={link.href}
                        color={color}
                        className="w-full md:w-auto"
                        onClick={() => trackCTA(label)}
                        aria-label={label}
                      >
                        {label}
                      </Button>
                    ))}
                    <Link
                      href="tel:3004 9681"
                      aria-label="Atendimento em 5 segundos no número 3004 9681"
                      onClick={() => trackCTA('3004 9681')}
                      className="flex flex-col flex-wrap flex-1 md:gap-0 items-center lg:items-start md:pt-0 max-w-5xl cursor-pointer"
                    >
                      <p className="max-w-5xl text-center md:text-left text-stone-300 paragraph-14">
                        Atendimento em 5 segundos
                      </p>
                      <span
                        className={`text-2xl text-center md:text-left font-bold whitespace-nowrap paragraph-24 text-display-0`}
                      >
                        3004 9681
                      </span>
                    </Link>
                  </div>
                  <HeroModal showModal={showModal} closeModal={closeModal} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default HeroBlackFriday;
